import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';

import Auth from './../../Auth';
import withTrack from 'HOCs/withTrack/withTrack';
import withConfig from 'HOCs/withConfig/withConfig';
import { PROFILE_SHOW } from 'Constants/tracking';
import { dayNameNumbers, getTimeToFormat } from 'Helpers/dates';
import { addressComponentsToString } from 'Helpers/locations';
import { KYC, USER_TYPE } from 'Constants/users';
import COCOSeller from './cards/COCOSeller';
import FOFOSeller from './cards/FOFOSeller';
import OtherSeller from './cards/OtherSeller';
import { convertArrayToCSVString } from 'Helpers/array';
import { getUserImage } from 'Helpers/images';
import ProfileHeaderCOCO from 'Components/Profile/ProfileHeader/ProfileHeaderCOCO';
import ProfileHeaderOther from 'Components/Profile/ProfileHeader/ProfileHeaderOther';
import { isCocoFofoUser } from 'Helpers/item';
import { IS_DESKTOP } from 'Constants/device.APP_TARGET';
import { TrackerOrigins } from 'Components/TrackerProvider/TrackerProvider';
import { userIsLogged, getLoggedUser } from 'Selectors/user';

const SELLER_CARD_COMPONENT = {
    [USER_TYPE.COCO.toLowerCase()]: COCOSeller,
    [USER_TYPE.FOFO.toLowerCase()]: FOFOSeller,
    other: OtherSeller
};

export const SellerInfo = ({
    user,
    item,
    track,
    config,
    getTrackingInfo,
    sellerProfilePage,
    showFollowSection,
    showVerifiedAccounts,
    showReportUser,
    intl,
    isMyItem,
    isClassified,
    actionButtons,
    meta,
    followers,
    following,
    userIsLogged,
    adpvTxnRevamp,
    loggedUser,
    coverImages,
    getVwoTrackingInfo,
    escrowDetails
}) => {
    const {
        id: userId, name: userName,
        dealer: {
            car: { dealer_type: userType } = {}
        } = {},
        kyc: {
            status: isUserKYCVerified
        } = {}
    } = user;
    const {
        showroom_address: {
            operatingdetails,
            addressLine1,
            addressLine2,
            city,
            landmark,
            latitude,
            longitude,
            locality,
            currentOpenState,
            isOpen,
            state
        } = {}
    } = { showroom_address: user.showroom_address || undefined };
    const {
        metadata: { locations: itemLocations } = {},
        display_date,
        created_at_first,
        created_at,
        is_kyc_verified_user: isItemKYCVerified
    } = item;
    const userLogged = Auth.getUser();

    const userImage = useMemo(() => {
        const staticAsseturl = config.get('staticAssets');
        const theme = config.get('theme', 'id');

        return getUserImage(staticAsseturl, user, '', theme);
    }, [user, config]);

    const onClickProfile = useCallback(() => track(PROFILE_SHOW, {
        ...getTrackingInfo(),
        ...getVwoTrackingInfo(),
        origin: TrackerOrigins.VIEW_ITEM,
        select_from: 'other_profile'
    }), [track, getTrackingInfo, getVwoTrackingInfo]);

    const profileLink = useMemo(() => {
        const staticProfileUrl = '/profile';
        const isLoggedInUserItem = userLogged && userLogged.id === userId;

        return isLoggedInUserItem ? staticProfileUrl : `${staticProfileUrl}/${userId}`;
    }, [userLogged, userId]);

    const address = useMemo(() => {
        if (isCocoFofoUser(userType)) {
            return convertArrayToCSVString([addressLine1, addressLine2, landmark, city, state]);
        }
        // for Regular/Preffered USERS
        const locationsObject = Array.isArray(itemLocations) && itemLocations.length > 0 && itemLocations[0].tree ? itemLocations[0].tree : [];
        const addressLimit = 3;

        return addressComponentsToString(locationsObject, addressLimit);
    }, [userType, itemLocations, addressLine1, addressLine2, landmark, city, state]);

    const formatOperatingTime = useCallback((weekdayIndex = 0, operatingdetails) => {
        if (operatingdetails && operatingdetails.length > 0) {
            const { startTime: startTimeStr, endTime: endTimeStr } = operatingdetails[weekdayIndex] || {};

            if (!endTimeStr) {
                return '';
            }
            const hourOpts = { hour: 'numeric', hour12: true };
            const hourMinOpts = { ...hourOpts, minute: 'numeric' };
            const startTime = getTimeToFormat(startTimeStr);
            const formattedStartTime = intl.formatTime(startTime, startTime.getMinutes() ? hourMinOpts : hourOpts)?.replace(/ 0/, ' 12');
            const endTime = getTimeToFormat(endTimeStr);
            const formattedEndTime = intl.formatTime(endTime, endTime.getMinutes() ? hourMinOpts : hourOpts)?.replace(/ 0/, ' 12');

            return `${formattedStartTime} - ${formattedEndTime}`
                .toUpperCase();
        }

        return '';
    }, [intl]);

    const getShortWeekDay = useCallback(operatingDay => {
        const dayNumber = dayNameNumbers[operatingDay];
        const dateToFormat = new Date(0, 0, dayNumber);

        return intl.formatDate(dateToFormat, { weekday: 'short' });
    }, [intl]);

    const sortWeekDays = arrToSort => {
        const daysSortOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        const newArrToSort = [...arrToSort];

        return newArrToSort.sort((a, b) => daysSortOrder.indexOf(a.operatingDay) - daysSortOrder.indexOf(b.operatingDay));
    };

    const generateOperatingObj = useCallback((operatingdetails, i) => ({
        ...operatingdetails[i],
        shortWeekDay: getShortWeekDay(operatingdetails[i].operatingDay),
        operatingTime: formatOperatingTime(i, operatingdetails)
    }), [formatOperatingTime, getShortWeekDay]);

    const operatingDetailsObject = useMemo(() => {
        const timeRanges = {};

        if (operatingdetails) {
            const sortedOperationDetails = sortWeekDays(operatingdetails);

            for (let i = 0; i < sortedOperationDetails.length; i++) {
                const currentTimeRange = `${sortedOperationDetails[i].startTime}-${sortedOperationDetails[i].endTime}`;

                if (Object.prototype.hasOwnProperty.call(timeRanges, currentTimeRange)) {
                    timeRanges[currentTimeRange].push(generateOperatingObj(sortedOperationDetails, i));
                }
                else {
                    timeRanges[currentTimeRange] = [generateOperatingObj(sortedOperationDetails, i)];
                }
            }
        }
        return timeRanges;
    }, [operatingdetails, generateOperatingObj]);

    const isCurrentlyOpen = () => {
        const now = new Date();
        const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

        // Get current local day and time
        const currentDay = daysOfWeek[now.getDay()];
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        const currentTime = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;

        // Check if the showroom is open
        return user?.showroom_address?.operatingdetails?.some(detail =>
            detail.operatingDay === currentDay
            && detail.isOpen
            && currentTime >= detail.startTime
            && currentTime <= detail.endTime
        );
    };

    let SellerCard;

    if (sellerProfilePage && IS_DESKTOP) {
        SellerCard = userType === USER_TYPE.COCO ? ProfileHeaderCOCO : ProfileHeaderOther;
    }
    else {
        SellerCard = SELLER_CARD_COMPONENT[(userType || '').toLowerCase()] || SELLER_CARD_COMPONENT.other;
    }
    return (
        <SellerCard
            userImage={ sellerProfilePage ? userImage : '' }
            userName={ userName }
            locality={ locality }
            address={ address }
            isClassified={ isClassified }
            currentOpenState={ currentOpenState }
            operatingDetailsObject={ operatingDetailsObject }
            profileLink={ profileLink }
            onClickProfile={ onClickProfile }
            latitude={ String(latitude) }
            item={ item }
            escrowDetails={ escrowDetails }
            longitude={ String(longitude) }
            isLoggedInUserItem={ userLogged && userLogged.id === userId }
            sellerProfilePage={ sellerProfilePage }
            user={ user }
            showFollowSection={ showFollowSection }
            showVerifiedAccounts={ showVerifiedAccounts }
            showReportUser={ showReportUser }
            postedDate={ display_date || created_at_first || created_at }
            isVerifiedSeller={ !!(isUserKYCVerified === KYC.VERIFIED || isItemKYCVerified) }
            isMyItem={ isMyItem }
            actionButtons={ actionButtons }
            showMemberSince={ sellerProfilePage }
            isOpen={ isOpen }
            meta={ meta }
            followers={ followers }
            following={ following }
            userIsLogged={ userIsLogged }
            loggedUser={ loggedUser }
            adpvTxnRevamp={ adpvTxnRevamp }
            isCurrentlyOpen={ isCurrentlyOpen() }
            coverImages={ coverImages }
        />
    );
};

SellerInfo.propTypes = {
    user: PropTypes.object,
    item: PropTypes.object,
    config: PropTypes.shape({
        get: PropTypes.func.isRequired
    }),
    track: PropTypes.func,
    getTrackingInfo: PropTypes.func,
    sellerProfilePage: PropTypes.bool,
    isClassified: PropTypes.bool,
    showFollowSection: PropTypes.bool,
    showVerifiedAccounts: PropTypes.bool,
    showReportUser: PropTypes.bool,
    intl: PropTypes.shape({
        formatDate: PropTypes.func.isRequired,
        formatTime: PropTypes.func.isRequired
    }).isRequired,
    isMyItem: PropTypes.bool,
    actionButtons: PropTypes.object,
    meta: PropTypes.func,
    followers: PropTypes.array,
    following: PropTypes.array,
    userIsLogged: PropTypes.bool,
    loggedUser: PropTypes.object,
    adpvTxnRevamp: PropTypes.bool,
    coverImages: PropTypes.array,
    getVwoTrackingInfo: PropTypes.func,
    escrowDetails: PropTypes.object
};

SellerInfo.defaultProps = {
    user: {},
    item: {},
    track: () => {},
    getTrackingInfo: () => {},
    sellerProfilePage: false,
    showFollowSection: false,
    showVerifiedAccounts: false,
    isClassified: false,
    showReportUser: false,
    isMyItem: false,
    actionButtons: null,
    adpvTxnRevamp: false
};

export const mapStateToProps = state => {
    return {
        userIsLogged: userIsLogged(state),
        loggedUser: getLoggedUser(state)
    };
};

export default compose(
    withConfig,
    withTrack,
    injectIntl,
    connect(mapStateToProps)
)(SellerInfo);
